import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Lock, Database, Mail, Cookie } from 'lucide-react';
import { motion } from 'framer-motion';
export function PrivacyPolicy() {
  const sections = [{
    icon: <Database className="h-6 w-6" data-id="element-1350" />,
    title: 'What Information We Collect',
    content: [{
      subtitle: 'Information You Provide',
      items: ['Account information (name, email, password)', 'Profile information (pronouns, bio, location - all optional)', 'Course enrollment and participation data', 'Payment information (processed by Stripe - we never see your full card number)', 'Communications with us (emails, support requests, feedback)']
    }, {
      subtitle: 'Information Collected Automatically',
      items: ['Device and browser information', 'IP address and general location (city/state level)', 'Pages visited and time spent on site', 'Referral source (how you found us)', 'Technical data for debugging and security']
    }, {
      subtitle: "Information We Don't Collect",
      items: ['We do not collect weight, BMI, or health data', 'We do not track you across other websites', 'We do not sell or share your data with advertisers', 'We do not use facial recognition or biometric data']
    }]
  }, {
    icon: <Eye className="h-6 w-6" data-id="element-1351" />,
    title: 'How We Use Your Information',
    content: [{
      subtitle: 'To Provide Our Services',
      items: ['Create and manage your account', 'Process course enrollments and payments', 'Send course materials and updates', 'Provide customer support', 'Improve our platform and content']
    }, {
      subtitle: 'To Communicate With You',
      items: ['Send transactional emails (receipts, password resets)', 'Send course-related updates and announcements', 'Send our monthly newsletter (if you opt in)', 'Respond to your questions and feedback', 'Notify you of policy changes']
    }, {
      subtitle: 'To Maintain Security',
      items: ['Prevent fraud and abuse', 'Monitor for security threats', 'Enforce our Terms of Service', 'Comply with legal obligations']
    }]
  }, {
    icon: <Lock className="h-6 w-6" data-id="element-1352" />,
    title: 'How We Protect Your Information',
    content: [{
      subtitle: 'Technical Safeguards',
      items: ['SSL/TLS encryption for all data transmission', 'Encrypted storage of sensitive data', 'Regular security audits and updates', 'Secure password hashing (bcrypt)', 'Two-factor authentication available']
    }, {
      subtitle: 'Organizational Safeguards',
      items: ['Limited employee access to personal data', 'Background checks for team members with data access', 'Regular privacy and security training', 'Incident response plan for data breaches', 'Annual third-party security assessments']
    }]
  }, {
    icon: <Shield className="h-6 w-6" data-id="element-1353" />,
    title: 'Who We Share Your Information With',
    content: [{
      subtitle: 'Service Providers',
      text: 'We share limited data with trusted service providers who help us operate ReGrowth:',
      items: ['Stripe (payment processing)', 'SendGrid (email delivery)', 'Zoom (live course sessions)', 'AWS (hosting and storage)', 'Google Analytics (website analytics - anonymized)'],
      note: 'All service providers are contractually required to protect your data and use it only for the services they provide to us.'
    }, {
      subtitle: 'Legal Requirements',
      text: 'We may disclose information if required by law, such as:',
      items: ['In response to valid subpoenas or court orders', 'To comply with legal obligations', 'To protect our rights or property', 'To prevent harm to individuals'],
      note: 'We will notify you of legal requests unless prohibited by law.'
    }, {
      subtitle: 'We Never Share With',
      items: ['Advertisers or marketing companies', 'Data brokers', 'Social media platforms (unless you explicitly connect your account)', 'Anyone else without your explicit consent']
    }]
  }];
  return <div className="min-h-screen bg-brand-cream" data-id="element-1354">
      {/* Header */}
      <header className="bg-white border-b-2 border-gray-100 py-6 sticky top-16 z-40" data-id="element-1355">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1356">
          <Link to="/policies" className="inline-flex items-center text-sm font-bold text-brand-navy hover:text-brand-cyan transition-colors" data-id="element-1357">
            <ArrowLeft className="h-4 w-4 mr-2" data-id="element-1358" />
            Back to Policies
          </Link>
        </div>
      </header>

      {/* Content */}
      <article className="py-16 px-4 sm:px-6 lg:px-8" data-id="element-1359">
        <div className="max-w-4xl mx-auto" data-id="element-1360">
          {/* Title */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="mb-12" data-id="element-1361">
            <span className="inline-block px-3 py-1 bg-brand-cyan text-white text-xs font-bold uppercase tracking-wider mb-6" data-id="element-1362">
              Privacy Policy
            </span>

            <h1 className="text-4xl md:text-5xl font-black leading-tight mb-6 text-brand-black" data-id="element-1363">
              Privacy Policy
            </h1>

            <p className="text-lg text-gray-600 mb-4" data-id="element-1364">
              Last updated: January 15, 2024
            </p>

            <div className="prose prose-lg" data-id="element-1365">
              <p className="text-xl text-gray-700 leading-relaxed" data-id="element-1366">
                At ReGrowth, we believe privacy is a right, not a privilege.
                This policy explains what information we collect, how we use it,
                and your rights regarding your data.
              </p>
            </div>
          </motion.div>

          {/* TL;DR */}
          <section className="mb-16" data-id="element-1367">
            <div className="bg-brand-cyan/10 border-l-4 border-brand-cyan p-8" data-id="element-1368">
              <h2 className="text-2xl font-black uppercase mb-4 text-brand-black" data-id="element-1369">
                TL;DR
              </h2>
              <ul className="space-y-2 text-gray-700" data-id="element-1370">
                <li data-id="element-1371">• We collect only what we need to provide our services</li>
                <li data-id="element-1372">
                  • We never sell your data to advertisers or third parties
                </li>
                <li data-id="element-1373">• You can download or delete your data at any time</li>
                <li data-id="element-1374">• We use industry-standard security measures</li>
                <li data-id="element-1375">• We're transparent about what we collect and why</li>
              </ul>
            </div>
          </section>

          {/* Main Sections */}
          {sections.map((section, index) => <motion.section key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="mb-16" data-id="element-1376">
              <div className="flex items-center gap-3 mb-6" data-id="element-1377">
                <div className="text-brand-cyan" data-id="element-1378">{section.icon}</div>
                <h2 className="text-3xl font-black uppercase text-brand-black" data-id="element-1379">
                  {section.title}
                </h2>
              </div>

              <div className="space-y-8" data-id="element-1380">
                {section.content.map((subsection, i) => <div key={i} className="bg-white border-2 border-brand-black p-6" data-id="element-1381">
                    <h3 className="text-xl font-bold mb-4 text-brand-navy" data-id="element-1382">
                      {subsection.subtitle}
                    </h3>
                    {subsection.text && <p className="text-gray-700 mb-4" data-id="element-1383">{subsection.text}</p>}
                    <ul className="space-y-2" data-id="element-1384">
                      {subsection.items.map((item, j) => <li key={j} className="flex items-start text-gray-700" data-id="element-1385">
                          <span className="text-brand-cyan mr-3 mt-1" data-id="element-1386">•</span>
                          <span data-id="element-1387">{item}</span>
                        </li>)}
                    </ul>
                    {subsection.note && <p className="text-sm text-gray-600 mt-4 italic border-t border-gray-100 pt-4" data-id="element-1388">
                        {subsection.note}
                      </p>}
                  </div>)}
              </div>
            </motion.section>)}

          {/* Cookies */}
          <section className="mb-16" data-id="element-1389">
            <div className="flex items-center gap-3 mb-6" data-id="element-1390">
              <Cookie className="h-6 w-6 text-brand-cyan" data-id="element-1391" />
              <h2 className="text-3xl font-black uppercase text-brand-black" data-id="element-1392">
                Cookies & Tracking
              </h2>
            </div>

            <div className="bg-white border-2 border-brand-black p-6 mb-6" data-id="element-1393">
              <h3 className="text-xl font-bold mb-4 text-brand-navy" data-id="element-1394">
                What Are Cookies?
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-1395">
                Cookies are small text files stored on your device that help
                websites remember your preferences and improve your experience.
              </p>

              <h4 className="font-bold text-brand-cyan mb-3 mt-6" data-id="element-1396">
                Essential Cookies (Required)
              </h4>
              <ul className="space-y-2 text-gray-700 mb-6" data-id="element-1397">
                <li className="flex items-start" data-id="element-1398">
                  <span className="text-brand-cyan mr-3" data-id="element-1399">•</span>
                  <span data-id="element-1400">Session cookies (keep you logged in)</span>
                </li>
                <li className="flex items-start" data-id="element-1401">
                  <span className="text-brand-cyan mr-3" data-id="element-1402">•</span>
                  <span data-id="element-1403">Security cookies (prevent fraud)</span>
                </li>
                <li className="flex items-start" data-id="element-1404">
                  <span className="text-brand-cyan mr-3" data-id="element-1405">•</span>
                  <span data-id="element-1406">Preference cookies (remember your settings)</span>
                </li>
              </ul>

              <h4 className="font-bold text-brand-cyan mb-3" data-id="element-1407">
                Analytics Cookies (Optional)
              </h4>
              <ul className="space-y-2 text-gray-700 mb-4" data-id="element-1408">
                <li className="flex items-start" data-id="element-1409">
                  <span className="text-brand-cyan mr-3" data-id="element-1410">•</span>
                  <span data-id="element-1411">Google Analytics (anonymized - no personal data)</span>
                </li>
                <li className="flex items-start" data-id="element-1412">
                  <span className="text-brand-cyan mr-3" data-id="element-1413">•</span>
                  <span data-id="element-1414">Help us understand how people use our site</span>
                </li>
              </ul>

              <p className="text-sm text-gray-600 bg-gray-50 p-4 border-l-4 border-brand-cyan" data-id="element-1415">
                You can disable analytics cookies in your browser settings or
                through our cookie preferences (available in the footer).
              </p>
            </div>
          </section>

          {/* Your Rights */}
          <section className="mb-16" data-id="element-1416">
            <h2 className="text-3xl font-black uppercase mb-6 text-brand-black" data-id="element-1417">
              Your Privacy Rights
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1418">
              <div className="bg-white border-2 border-brand-black p-6" data-id="element-1419">
                <h3 className="font-bold text-lg mb-3 text-brand-navy" data-id="element-1420">
                  Access Your Data
                </h3>
                <p className="text-gray-700 text-sm mb-3" data-id="element-1421">
                  Request a copy of all personal data we have about you.
                </p>
                <p className="text-xs text-gray-600" data-id="element-1422">
                  Email{' '}
                  <a href="mailto:privacy@regrowth.com" className="text-brand-cyan hover:underline" data-id="element-1423">
                    privacy@regrowth.com
                  </a>
                </p>
              </div>

              <div className="bg-white border-2 border-brand-black p-6" data-id="element-1424">
                <h3 className="font-bold text-lg mb-3 text-brand-navy" data-id="element-1425">
                  Correct Your Data
                </h3>
                <p className="text-gray-700 text-sm mb-3" data-id="element-1426">
                  Update or correct inaccurate information in your account.
                </p>
                <p className="text-xs text-gray-600" data-id="element-1427">
                  Available in Account Settings
                </p>
              </div>

              <div className="bg-white border-2 border-brand-black p-6" data-id="element-1428">
                <h3 className="font-bold text-lg mb-3 text-brand-navy" data-id="element-1429">
                  Delete Your Data
                </h3>
                <p className="text-gray-700 text-sm mb-3" data-id="element-1430">
                  Request permanent deletion of your account and data.
                </p>
                <p className="text-xs text-gray-600" data-id="element-1431">
                  Email{' '}
                  <a href="mailto:privacy@regrowth.com" className="text-brand-cyan hover:underline" data-id="element-1432">
                    privacy@regrowth.com
                  </a>
                </p>
              </div>

              <div className="bg-white border-2 border-brand-black p-6" data-id="element-1433">
                <h3 className="font-bold text-lg mb-3 text-brand-navy" data-id="element-1434">
                  Export Your Data
                </h3>
                <p className="text-gray-700 text-sm mb-3" data-id="element-1435">
                  Download your data in a portable format (JSON, CSV).
                </p>
                <p className="text-xs text-gray-600" data-id="element-1436">
                  Available in Account Settings
                </p>
              </div>

              <div className="bg-white border-2 border-brand-black p-6" data-id="element-1437">
                <h3 className="font-bold text-lg mb-3 text-brand-navy" data-id="element-1438">
                  Opt Out of Marketing
                </h3>
                <p className="text-gray-700 text-sm mb-3" data-id="element-1439">
                  Unsubscribe from newsletters and promotional emails.
                </p>
                <p className="text-xs text-gray-600" data-id="element-1440">
                  Click "unsubscribe" in any email
                </p>
              </div>

              <div className="bg-white border-2 border-brand-black p-6" data-id="element-1441">
                <h3 className="font-bold text-lg mb-3 text-brand-navy" data-id="element-1442">
                  Object to Processing
                </h3>
                <p className="text-gray-700 text-sm mb-3" data-id="element-1443">
                  Object to certain uses of your data (e.g., analytics).
                </p>
                <p className="text-xs text-gray-600" data-id="element-1444">
                  Email{' '}
                  <a href="mailto:privacy@regrowth.com" className="text-brand-cyan hover:underline" data-id="element-1445">
                    privacy@regrowth.com
                  </a>
                </p>
              </div>
            </div>

            <div className="mt-6 bg-brand-cyan/10 border-l-4 border-brand-cyan p-6" data-id="element-1446">
              <p className="text-sm text-gray-700" data-id="element-1447">
                <strong data-id="element-1448">Response Time:</strong> We respond to all privacy
                requests within 30 days. For complex requests, we may extend
                this to 60 days and will notify you.
              </p>
            </div>
          </section>

          {/* Data Retention */}
          <section className="mb-16" data-id="element-1449">
            <h2 className="text-3xl font-black uppercase mb-6 text-brand-black" data-id="element-1450">
              How Long We Keep Your Data
            </h2>

            <div className="prose prose-lg max-w-none" data-id="element-1451">
              <div className="bg-white border-2 border-brand-black p-6" data-id="element-1452">
                <ul className="space-y-4 text-gray-700" data-id="element-1453">
                  <li className="flex items-start" data-id="element-1454">
                    <span className="font-bold text-brand-cyan mr-3 min-w-[120px]" data-id="element-1455">
                      Account data:
                    </span>
                    <span data-id="element-1456">
                      Until you delete your account, then 30 days for backup
                      purposes
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1457">
                    <span className="font-bold text-brand-cyan mr-3 min-w-[120px]" data-id="element-1458">
                      Course data:
                    </span>
                    <span data-id="element-1459">
                      For the duration of your enrollment plus 1 year for
                      records
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1460">
                    <span className="font-bold text-brand-cyan mr-3 min-w-[120px]" data-id="element-1461">
                      Payment data:
                    </span>
                    <span data-id="element-1462">7 years (required by law for tax purposes)</span>
                  </li>
                  <li className="flex items-start" data-id="element-1463">
                    <span className="font-bold text-brand-cyan mr-3 min-w-[120px]" data-id="element-1464">
                      Analytics data:
                    </span>
                    <span data-id="element-1465">
                      26 months (Google Analytics default), then automatically
                      deleted
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1466">
                    <span className="font-bold text-brand-cyan mr-3 min-w-[120px]" data-id="element-1467">
                      Support emails:
                    </span>
                    <span data-id="element-1468">3 years for reference and quality improvement</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Children's Privacy */}
          <section className="mb-16" data-id="element-1469">
            <h2 className="text-3xl font-black uppercase mb-6 text-brand-black" data-id="element-1470">
              Children's Privacy
            </h2>
            <div className="bg-white border-2 border-brand-black p-6" data-id="element-1471">
              <p className="text-gray-700 mb-4" data-id="element-1472">
                ReGrowth is not intended for children under 16. We do not
                knowingly collect personal information from children under 16.
              </p>
              <p className="text-gray-700" data-id="element-1473">
                If you believe we have collected information from a child under
                16, please contact us immediately at{' '}
                <a href="mailto:privacy@regrowth.com" className="text-brand-cyan font-bold hover:underline" data-id="element-1474">
                  privacy@regrowth.com
                </a>{' '}
                and we will delete it.
              </p>
            </div>
          </section>

          {/* International Users */}
          <section className="mb-16" data-id="element-1475">
            <h2 className="text-3xl font-black uppercase mb-6 text-brand-black" data-id="element-1476">
              International Users
            </h2>
            <div className="bg-white border-2 border-brand-black p-6" data-id="element-1477">
              <p className="text-gray-700 mb-4" data-id="element-1478">
                ReGrowth is based in the United States. If you access our
                services from outside the U.S., your data will be transferred to
                and processed in the U.S.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-1479">
                <strong data-id="element-1480">For EU/UK users:</strong> We comply with GDPR
                requirements, including:
              </p>
              <ul className="space-y-2 text-gray-700 mb-4" data-id="element-1481">
                <li className="flex items-start" data-id="element-1482">
                  <span className="text-brand-cyan mr-3" data-id="element-1483">•</span>
                  <span data-id="element-1484">Standard Contractual Clauses for data transfers</span>
                </li>
                <li className="flex items-start" data-id="element-1485">
                  <span className="text-brand-cyan mr-3" data-id="element-1486">•</span>
                  <span data-id="element-1487">
                    Your right to lodge a complaint with your data protection
                    authority
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1488">
                  <span className="text-brand-cyan mr-3" data-id="element-1489">•</span>
                  <span data-id="element-1490">
                    All GDPR rights listed in "Your Privacy Rights" above
                  </span>
                </li>
              </ul>
              <p className="text-gray-700" data-id="element-1491">
                <strong data-id="element-1492">For California users:</strong> See our California
                Privacy Notice below for CCPA-specific rights.
              </p>
            </div>
          </section>

          {/* Changes to Policy */}
          <section className="mb-16" data-id="element-1493">
            <h2 className="text-3xl font-black uppercase mb-6 text-brand-black" data-id="element-1494">
              Changes to This Policy
            </h2>
            <div className="bg-white border-2 border-brand-black p-6" data-id="element-1495">
              <p className="text-gray-700 mb-4" data-id="element-1496">
                We may update this Privacy Policy from time to time. When we
                make significant changes:
              </p>
              <ul className="space-y-2 text-gray-700 mb-4" data-id="element-1497">
                <li className="flex items-start" data-id="element-1498">
                  <span className="text-brand-cyan mr-3" data-id="element-1499">•</span>
                  <span data-id="element-1500">
                    We'll email you at least 30 days before changes take effect
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1501">
                  <span className="text-brand-cyan mr-3" data-id="element-1502">•</span>
                  <span data-id="element-1503">We'll post a notice on our website</span>
                </li>
                <li className="flex items-start" data-id="element-1504">
                  <span className="text-brand-cyan mr-3" data-id="element-1505">•</span>
                  <span data-id="element-1506">
                    We'll update the "Last updated" date at the top of this page
                  </span>
                </li>
              </ul>
              <p className="text-gray-700" data-id="element-1507">
                Continued use of ReGrowth after changes take effect means you
                accept the updated policy.
              </p>
            </div>
          </section>

          {/* Contact */}
          <section className="mb-16" data-id="element-1508">
            <div className="bg-brand-navy text-white p-8 border-2 border-brand-black" data-id="element-1509">
              <div className="flex items-start gap-4" data-id="element-1510">
                <Mail className="h-8 w-8 flex-shrink-0 mt-1" data-id="element-1511" />
                <div data-id="element-1512">
                  <h2 className="text-2xl font-black uppercase mb-4" data-id="element-1513">
                    Questions About Privacy?
                  </h2>
                  <p className="text-gray-200 mb-4" data-id="element-1514">
                    If you have questions about this Privacy Policy or how we
                    handle your data:
                  </p>
                  <div className="space-y-2 text-gray-200" data-id="element-1515">
                    <p data-id="element-1516">
                      <strong data-id="element-1517">Email:</strong>{' '}
                      <a href="mailto:privacy@regrowth.com" className="text-brand-cyan hover:underline" data-id="element-1518">
                        privacy@regrowth.com
                      </a>
                    </p>
                    <p data-id="element-1519">
                      <strong data-id="element-1520">Mail:</strong> ReGrowth Privacy Team, [Address]
                    </p>
                    <p data-id="element-1521">
                      <strong data-id="element-1522">Response time:</strong> Within 2 business days
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Footer */}
          <div className="pt-8 border-t-2 border-gray-200" data-id="element-1523">
            <p className="text-sm text-gray-600 mb-4" data-id="element-1524">
              This privacy policy was last reviewed and updated on January 15,
              2024.
            </p>
            <p className="text-sm text-gray-600" data-id="element-1525">
              <strong data-id="element-1526">Version:</strong> 2.0 |{' '}
              <Link to="/policies" className="text-brand-cyan hover:underline" data-id="element-1527">
                View all policies
              </Link>
            </p>
          </div>
        </div>
      </article>
    </div>;
}